/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { Alert, Layout, Icon, Popconfirm, message, Tooltip, Spin } from 'antd';
import Button from '../../../../components/Button/Button';
import Report from '../../components/Report/Report';
import HazardIndexItem from '../../components/HazardIndexItem/HazardIndexItem';
import {
  hazardIndexSelector,
  industryIdSelector,
  loadingHazardIndexSelector,
} from '../../../Industry/IndustrySelectors';
import { underwriterHazardIndexCustomizationSelector, sectionsSelector } from '../../ReportSelectors';
import {
  updateReportCustomizations,
  clearReportCustomization,
  fetchReportCustomization,
  resetHazardIndexChartDataUrl,
} from '../../ReportActions';
import { fetchRiskChecklist } from '../../../Industry/IndustryActions';
import './UnderwriterReport.scss';

/** Insurance class ids from API/JSON vary string vs number — compare and merge with coercion. */
function normalizeUwClassId(raw) {
  if (raw == null || raw === '') return raw;
  const n = Number(raw);
  return Number.isNaN(n) ? raw : n;
}

function uwClassIdsEqual(a, b) {
  return normalizeUwClassId(a) === normalizeUwClassId(b);
}

const { Content, Header } = Layout;

const UnderwriterReport = ({
  fullscreen,
  setFullscreen,
  industryId,
  sections,
  customization,
  hazardIndexes = [],
  clearReportCustomization,
  fetchReportCustomization,
  fetchRiskChecklist,
  updateReportCustomizations,
  loadingCustomizations,
  loadingHazardIndex,
  resetHazardIndexChartDataUrl,
}) => {
  const [selected, setSelected] = useState([]);

  const [page, setPage] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('tmpl') ? 'report' : 'underwriter';
    } catch {
      return 'underwriter';
    }
  });

  const location = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('tmpl')) {
      fetchReportCustomization(query.get('tmpl'));
    }
  }, [location, fetchReportCustomization]);

  useEffect(() => {
    if (!industryId) return;
    fetchRiskChecklist(industryId, { type: 'Key' });
  }, [industryId, fetchRiskChecklist]);

  const persistSelection = useCallback(
    async (selectedClasses) => {
      if (loadingCustomizations || !industryId) return;

      /** Baseline rows from Redux (empty until first save). Omitting `report_id` on first POST creates the draft. */
      const baselineClasses = Array.isArray(customization?.customizations)
        ? customization.customizations
        : [];

      const selectedNormalized = selectedClasses.map(normalizeUwClassId);

      const selectedClassesToSave = selectedNormalized.map((selectedClassId) => {
        const classFound = baselineClasses.find((hazardIndexRow) =>
          uwClassIdsEqual(hazardIndexRow?.id, selectedClassId)
        );
        return {
          ...(classFound || {}),
          id: selectedClassId,
          checked: true,
        };
      });

      const hazardIndexWithComments = baselineClasses
        .filter((hazardIndexRow) => {
          const baselineId = hazardIndexRow?.id;
          const isStillSelected = selectedNormalized.some((sid) => uwClassIdsEqual(sid, baselineId));
          return (
            !isStillSelected &&
            (hazardIndexRow.comments || hazardIndexRow.checklist?.length)
          );
        })
        .map((hazardIndex) => ({
          ...hazardIndex,
          checked: false,
        }));

      try {
        await updateReportCustomizations(industryId, customization?.id, customization?.name ?? 'Insured Business Name', [
          {
            section: 'hazard_index_underwriter',
            customization: JSON.stringify({ classes: [...selectedClassesToSave, ...hazardIndexWithComments] }),
          },
        ]);
        resetHazardIndexChartDataUrl();
        message.success('Saved');
      } catch (err) {
        const detail = err?.response?.data?.detail ?? err?.response?.data?.title;
        const text =
          (typeof detail === 'string' && detail) ||
          (typeof err?.message === 'string' && err.message) ||
          'Unable to save. Please try again.';
        message.error(text);
      }
    },
    [
      loadingCustomizations,
      customization,
      industryId,
      updateReportCustomizations,
      resetHazardIndexChartDataUrl,
    ]
  );

  const persistSelectionRef = useRef(persistSelection);
  persistSelectionRef.current = persistSelection;

  const debouncedPersistSelection = useMemo(
    () =>
      debounce((selectedClasses) => {
        persistSelectionRef.current(selectedClasses);
      }, 500),
    []
  );

  useEffect(
    () => () => {
      if (debouncedPersistSelection.cancel) debouncedPersistSelection.cancel();
    },
    [debouncedPersistSelection]
  );

  useEffect(() => {
    if (customization?.customizations && customization.customizations.length > 0) {
      const preSelectedClasses = customization.customizations
        .filter((hazardIndexRow) => hazardIndexRow?.checked)
        .map((hazardIndexRow) => normalizeUwClassId(hazardIndexRow?.id))
        .filter((id) => id != null && !Number.isNaN(Number(id)));
      setSelected(preSelectedClasses);
    }
  }, [customization]);

  useEffect(() => {
    if (
      sections &&
      sections['report-hazard-assessment'] &&
      sections['report-hazard-assessment'].children &&
      sections['report-hazard-assessment'].children.length
    ) {
      setSelected(sections['report-hazard-assessment'].children.map((hazardId) => +hazardId));
    }
  }, [sections]);

  const handleToggleClassClick = useCallback(
    (id) => {
      if (loadingCustomizations) return;
      const nextSelectedIds = selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id];
      setSelected(nextSelectedIds);
      debouncedPersistSelection(nextSelectedIds);
    },
    [selected, loadingCustomizations, debouncedPersistSelection]
  );

  const handleTileKeyDown = useCallback(
    (e, id) => {
      if (loadingCustomizations) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggleClassClick(id);
      }
    },
    [loadingCustomizations, handleToggleClassClick]
  );

  const activeHazardIndexes = useMemo(() => hazardIndexes || [], [hazardIndexes]);
  const hasNoHazardIndex = !loadingHazardIndex && Array.isArray(hazardIndexes) && hazardIndexes.length === 0;
  const unavailableDownloadMessage =
    'This report is not available for download because no hazard index data is available for this industry.';
  const nextDisabled = hasNoHazardIndex || selected.length === 0;
  let selectionSummary = `${selected.length} insurance class${selected.length === 1 ? '' : 'es'} selected`;
  if (selected.length === 0) selectionSummary = 'No classes selected yet';
  if (hasNoHazardIndex) selectionSummary = 'No hazard index data available';
  let nextTooltipTitle = null;
  if (nextDisabled) nextTooltipTitle = 'Select at least one insurance class to continue.';
  if (hasNoHazardIndex) nextTooltipTitle = unavailableDownloadMessage;

  switch (page) {
    case 'underwriter':
      return (
        <Content>
          <Header className="report-header underwriter-report-header">
            <h3 className="underwriter-report-heading">Underwriter Report</h3>
            <div className="underwriter-report-header-actions">
              <Popconfirm
                title="This clears your saved class selection and report customizations for this draft. Continue?"
                onConfirm={() => {
                  if (debouncedPersistSelection.cancel) debouncedPersistSelection.cancel();
                  setSelected([]);
                  clearReportCustomization();
                  resetHazardIndexChartDataUrl();
                }}
                okText="Yes, reset"
                cancelText="Cancel"
              >
                <Button secondary>
                  <Icon type="reload" /> Reset
                </Button>
              </Popconfirm>
              <Tooltip title={nextTooltipTitle} placement="bottomRight">
                <span className="underwriter-next-wrap">
                  <Button
                    secondary
                    type="primary"
                    disabled={nextDisabled}
                    onClick={() => setPage('report')}
                  >
                    Next <Icon type="right" />
                  </Button>
                </span>
              </Tooltip>
            </div>
          </Header>
          <main className="underwriter-main">
            <h3 className="underwriter-section-title">Report · Hazard assessment</h3>
            <div className="underwriter-description">
              To tailor this report, <strong>select the insurance classes</strong> for consideration and{' '}
              <strong>add your comments</strong> to each.
            </div>
            <div className="underwriter-selection-summary" role="status" aria-live="polite">
              <span className="underwriter-selection-count">{selectionSummary}</span>
              {!hasNoHazardIndex && selected.length > 0 && (
                <span className="underwriter-selection-hint">Click a selected card again to remove it.</span>
              )}
            </div>
            {hasNoHazardIndex && (
              <Alert
                className="report-unavailable-alert"
                type="warning"
                showIcon
                message="Report not available for download"
                description={unavailableDownloadMessage}
              />
            )}
            <Spin spinning={loadingCustomizations} tip="Saving…">
              <div
                className={classNames('hazard-index-container', {
                  'is-saving': loadingCustomizations,
                })}
              >
                {activeHazardIndexes.map((data, index) => {
                  const id = +data.id;
                  const isSelected = selected.includes(id);
                  return (
                    <div
                      key={data.id}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={loadingCustomizations ? -1 : 0}
                      className={classNames('hazard-index-item-container', { selected: isSelected })}
                      onClick={() => handleToggleClassClick(id)}
                      onKeyDown={(e) => handleTileKeyDown(e, id)}
                      aria-label={`${isSelected ? 'Deselect' : 'Select'} ${data.name} for this report`}
                    >
                      <div className="icon-container" aria-hidden="true">
                        <Icon
                          type={isSelected ? 'check-circle' : 'check-circle-o'}
                          theme={isSelected ? 'filled' : undefined}
                          className="underwriter-tile-check"
                        />
                      </div>
                      <HazardIndexItem appearance="tile" data={data} index={index} underwriter />
                    </div>
                  );
                })}
              </div>
            </Spin>
          </main>
        </Content>
      );

    case 'report': {
      return (
        <Report
          handleBack={() => {
            setPage('underwriter');
          }}
          fullscreen={fullscreen}
          setFullscreen={setFullscreen}
          type="Underwriter Report"
          underwriter
        />
      );
    }
    default:
      return <div />;
  }
};

const mapStateToProps = (state) => ({
  customization: underwriterHazardIndexCustomizationSelector(state),
  hazardIndexes: hazardIndexSelector(state),
  industryId: industryIdSelector(state),
  loadingCustomizations: state.report.loadingCustomizations,
  loadingHazardIndex: loadingHazardIndexSelector(state),
  sections: sectionsSelector(state),
});

const mapDispatchToProps = {
  updateReportCustomizations,
  clearReportCustomization,
  fetchReportCustomization,
  resetHazardIndexChartDataUrl,
  fetchRiskChecklist,
};

export default connect(mapStateToProps, mapDispatchToProps)(UnderwriterReport);
